import { motion } from "framer-motion";
import { ArrowRight, BookOpen, Code2, Database, Server } from "lucide-react";
import { useRouter } from "../context/RouterContext";
import { useEffect, useState } from "react";
import { Article, StrapiData } from "../types";
import BlinkingAsciiDots from "../components/BlinkingAsciiDots";
import api from "../services/api";
import { useLocale } from "../context/LocaleContext";
import { ArticleCard } from "../components/ArticleCard";

interface HeroData {
  title_pt1: string;
  title_pt2: string;
  title_pt3: string;
  text: string;
  btn_left: string;
  btn_right: string;
  featured_articles_title: string;
  view_all: string;
  read_article: string;
  min_read: string;
}

interface StrapiResponse<T> {
  data: T;
  meta?: any;
}

export function HomePage() {
  const { navigate } = useRouter();
  const { locale } = useLocale();
  const [featuredArticles, setFeaturedArticles] = useState<Article[]>([]);
  const [heroData, setHeroData] = useState<HeroData>({
    title_pt1: "Learning",
    title_pt2: "DevOps",
    title_pt3: "One Day at a Time",
    text: "Join me on my DevOps journey as I share what I'm learning every day from infrastructure and automation to best practices and real-world challenges.",
    btn_left: "Explore Articles",
    btn_right: "View Resources",
    featured_articles_title: "Featured Articles",
    view_all: "View All",
    read_article: "Read Article",
    min_read: "min read",
  });

  useEffect(() => {
    async function fetchData() {
      try {
        // Fetch homepage data from Strapi
        const homepageResponse = (await api.getHomepage(
          locale
        )) as StrapiResponse<{ hero: HeroData }>;
        if (homepageResponse?.data?.hero) {
          setHeroData(homepageResponse.data.hero);
        }

        // Fetch featured articles (limit to 3)
        const articlesResponse: StrapiData<Article[]> = await api.getArticles(
          locale,
          {
            filters: { featured: { $eq: true } },
            pagination: { pageSize: 3 },
          }
        );
        if (articlesResponse?.data) {
          setFeaturedArticles(articlesResponse.data);
        }
      } catch (error) {
        console.error("Error fetching homepage data:", error);
      }
    }

    fetchData();
  }, [locale]);

  const features = [
    {
      icon: Server,
      title: "Infrastructure",
      description:
        "Learn about cloud platforms, containerization, and orchestration tools.",
    },
    {
      icon: Code2,
      title: "CI/CD",
      description: "Master continuous integration and deployment pipelines.",
    },
    {
      icon: Database,
      title: "Monitoring",
      description: "Implement robust monitoring and logging solutions.",
    },
    {
      icon: BookOpen,
      title: "Best Practices",
      description: "Follow industry-standard DevOps methodologies.",
    },
  ];

  return (
    <div className="min-h-screen bg-black">
      <motion.section
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="relative py-60 px-4 overflow-hidden"
      >
        <BlinkingAsciiDots
          backgroundColor="#000000"
          textColor="220, 38, 38"
          density={1}
          animationSpeed={0.75}
          removeWaveLine={true}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-red-900/10 via-transparent to-transparent pointer-events-none" />

        <div className="container mx-auto relative z-10">
          <motion.div
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="text-center max-w-4xl mx-auto"
          >
            <motion.h1
              className="text-5xl md:text-7xl font-bold mb-6 leading-relaxed"
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.3 }}
            >
              <div className="mb-4">
                {heroData.title_pt1}{" "}
                <span className="text-red-600">{heroData.title_pt2}</span>
              </div>
              <div>{heroData.title_pt3}</div>
            </motion.h1>
            <motion.p
              className="text-xl text-gray-400 mb-8"
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.4 }}
            >
              {heroData.text}
            </motion.p>
            <motion.div
              className="flex flex-wrap gap-4 justify-center"
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.5 }}
            >
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate("articles")}
                className="bg-red-600 hover:bg-red-700 text-white px-8 py-3 rounded-lg font-semibold flex items-center gap-2 transition-colors"
              >
                {heroData.btn_left}
                <ArrowRight className="w-5 h-5" />
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate("resources")}
                className="bg-white/10 hover:bg-white/20 text-white px-8 py-3 rounded-lg font-semibold transition-colors"
              >
                {heroData.btn_right}
              </motion.button>
            </motion.div>
          </motion.div>
        </div>
      </motion.section>

      {featuredArticles.length > 0 && (
        <section className="py-20 px-4 bg-gray-900">
          <div className="container mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="flex justify-between items-center mb-12"
            >
              <h2 className="text-3xl md:text-4xl font-bold">
                {(() => {
                  const title =
                    heroData.featured_articles_title || "Featured Articles";
                  const words = title.split(" ");

                  // For Turkish (tr-TR), only make the last word red
                  // For English and others, make all words after the first red
                  if (locale === "tr-TR") {
                    const lastWord = words[words.length - 1];
                    const otherWords = words.slice(0, -1).join(" ");
                    return (
                      <>
                        {otherWords}{" "}
                        <span className="text-red-600">{lastWord}</span>
                      </>
                    );
                  } else {
                    const firstWord = words[0];
                    const restWords = words.slice(1).join(" ");
                    return (
                      <>
                        {firstWord}{" "}
                        <span className="text-red-600">{restWords}</span>
                      </>
                    );
                  }
                })()}
              </h2>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate("articles")}
                className="text-red-600 hover:text-red-500 flex items-center gap-2"
              >
                {heroData.view_all}
                <ArrowRight className="w-5 h-5" />
              </motion.button>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {featuredArticles.length > 0 &&
                featuredArticles.map((article, idx) => (
                  <ArticleCard
                    key={article.id}
                    article={article}
                    index={idx}
                    onClick={() => navigate("article", { slug: article.slug })}
                    readArticleText={heroData.read_article}
                    minReadText={heroData.min_read}
                  />
                ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
